import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { htmlEncode } from '../../../utils/escape.utils';
import { ResponsePanelItemWebComponent } from '../../responsePanelItem';
import { ActionCategory, CategoryType } from '../types';
import { Action } from '../../responsePanelItem/types';
import { GridViews } from '../../responseGrid/types';
import { html } from 'lit';

export class ResponsePanelView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$actionNeededTab = this.shadow.querySelector('#actionNeededTab');
    this.dom.$executedTab = this.shadow.querySelector('#executedTab');
    this.dom.$lastUpdated = this.shadow.querySelector('#lastUpdated');
    this.dom.$responseTitle = this.shadow.querySelector('#responseTitle');
    this.dom.$tabGroup = this.shadow.querySelector('gz-tab-group');
    this.dom.$refreshButton = this.shadow.querySelector('#refreshButton');
  }

  private setActionNeededEmptyState() {
    this.dom.$actionNeededTab.innerHTML =
      '<gz-image-repo state="success">' +
      '<gz-title fontSize="18" fontWeight="500" class="action-needed-title">' + this.dependencies.translateFn('No recommendations available.') + '</gz-title>' +
      '<gz-text fontSize="12" fontWeight="400" class="action-needed-empty-description">' + this.dependencies.translateFn('All recommended response actions for the extended incident will appear here.') + '</gz-text>' +
      '</gz-image-repo>';
  }

  private setExecutedEmptyState() {
    this.dom.$executedTab.innerHTML =
      '<gz-image-repo state="success">' +
      '<gz-title fontSize="18" fontWeight="500" class="executed-title">' + this.dependencies.translateFn('No actions were executed.') + '</gz-title>' +
      '<gz-text fontSize="12" fontWeight="400" class="executed-description">' + this.dependencies.translateFn('All executed response actions for the extended incident will appear here.') + '</gz-text>' +
      '</gz-image-repo>';
  }

  buildView(data: any) {
    const currentDate = new Date();

    this.dom.$lastUpdated.innerHTML = this.dependencies.translateFn('Last updated {date}', {
      date: `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`,
    }); // TODO proper timezone
    this.dom.$responseTitle.innerHTML = htmlEncode(this.dependencies.translateFn('response'));

    let actionNeededTabText = this.dependencies.translateFn('action needed');
    let executedTabText = this.dependencies.translateFn('executed');

    this.dom.$actionNeededTab.innerHTML = '';
    this.dom.$executedTab.innerHTML = '';

    if (!data || !data.response || (Array.isArray(data.response) && !data.response.length)) {
      this.dom.$actionNeededTab.label = htmlEncode(actionNeededTabText);
      this.dom.$executedTab.label = htmlEncode(executedTabText);

      this.setActionNeededEmptyState();
      this.setExecutedEmptyState();

      this.dom.$tabGroup.requestUpdate();

      return;
    }

    let numberOfNeededActions = 0;

    if (!data.response.actionNeeded || !data.response.actionNeeded.length) {
      this.setActionNeededEmptyState();
    } else {
      const recommendations = {};

      data.response.actionNeeded.forEach(action => {
        if (!recommendations[action._id.groupType]) {
          recommendations[action._id.groupType] = {};
        }

        recommendations[action._id.groupType][action._id.type] = action.count;
      });

      const actionNeeded: ActionCategory[] = [];

      Object.values(CategoryType).forEach(category => {
        if (!recommendations[category]) {
          return;
        }

        const actions: Action[] = [];

        for (const [key, value] of Object.entries(recommendations[category])) {
          actions.push({
            actionType: parseInt(key, 10),
            actionName: this.dependencies.translateRecommendationTypesForOverview(key),
            numberOfActions: value as number,
          });
        }

        actionNeeded.push({
          actionCategoryName: this.dependencies.translateRecommendationCategories(category),
          actions,
          category: parseInt(category.toString(), 10),
        });
      });

      for (const actionCategory of actionNeeded) {
        const component = new ResponsePanelItemWebComponent();
        component.actionCategoryName = actionCategory.actionCategoryName;
        component.actions = actionCategory.actions;
        component.view = GridViews.ACTIONS_NEEDED;
        component.category = actionCategory.category;

        for (const action of actionCategory.actions) {
          numberOfNeededActions += action.numberOfActions;
        }

        component.injectDependencies(this.dependencies);
        component.start().then().catch();
        this.dom.$actionNeededTab.append(component);
      }
    }

    let numberOfExecutedAction = 0;

    if (!data.response.executed || !data.response.executed.length) {
      this.setExecutedEmptyState();
    } else {
      const executed: Action[] = [];

      data.response.executed.forEach(action => {
        executed.push({
          actionName: this.dependencies.translateRecommendationTypesForOverview(action._id.type),
          actionType: action._id.type,
          numberOfActions: action.count,
        });
      });

      const responsePanelItemExecuted = new ResponsePanelItemWebComponent();
      responsePanelItemExecuted.actions = executed;
      responsePanelItemExecuted.view = GridViews.EXECUTED;
      responsePanelItemExecuted.injectDependencies(this.dependencies);
      responsePanelItemExecuted.start().then().catch();

      for (const action of executed) {
        numberOfExecutedAction += action.numberOfActions;
      }
      this.dom.$executedTab.append(responsePanelItemExecuted);
    }

    if (numberOfNeededActions) {
      actionNeededTabText += ` (${numberOfNeededActions})`;
    }
    this.dom.$actionNeededTab.label = htmlEncode(actionNeededTabText);

    if (numberOfExecutedAction) {
      executedTabText += ` (${numberOfExecutedAction})`;
    }
    this.dom.$executedTab.label = htmlEncode(executedTabText);

    this.dom.$tabGroup.requestUpdate();
  }
}
